import {
  MutationCache,
  QueryCache,
  QueryClient,
  notifyManager,
} from '@tanstack/react-query';
import { createRouter } from '@tanstack/react-router';
import { routeTree } from './routeTree.gen';
import { AppError } from './components/app-error';
import { NotFound } from './components/not-found';
import { isAuthError, isClientError } from './lib/app-error';
import { getPostHog } from './lib/posthog';
import { recoverSignedOutSession } from './lib/session-recovery';
import { createClient, createOptionsProxy } from './lib/trpc';

export function createAppRouter() {
  notifyManager.setScheduler((callback) =>
    window.requestAnimationFrame(callback),
  );

  // A session that dies mid-use fails every request from then on. Queries that
  // already hold data never throw to the dashboard's error boundary, so the
  // failure is caught here, once, for queries and mutations alike.
  const onError = (error: unknown) => {
    if (isAuthError(error)) {
      recoverSignedOutSession();
    }
  };
  const queryClient = new QueryClient({
    queryCache: new QueryCache({ onError }),
    mutationCache: new MutationCache({ onError }),
    defaultOptions: {
      queries: {
        // Live updates mark what changed as stale; this only spares refetching
        // on every mount while the socket is up.
        staleTime: 30_000,
        // A refusal says the same thing the second time.
        retry: (failureCount, error) =>
          failureCount < 2 && !isClientError(error),
        // An auth error is not a crash: `onError` above routes it to sign-in.
        throwOnError: (error) => !isAuthError(error),
      },
    },
  });
  const trpcClient = createClient();
  const trpc = createOptionsProxy(trpcClient, queryClient);

  return createRouter({
    routeTree,
    defaultPreload: 'intent',
    defaultPreloadDelay: 200,
    defaultPendingMs: 150,
    defaultPendingMinMs: 200,
    context: { queryClient, trpcClient, trpc },
    defaultErrorComponent: AppError,
    defaultOnCatch: (error) => {
      if (!isAuthError(error)) {
        getPostHog()?.captureException(error);
      }
    },
    defaultNotFoundComponent: NotFound,
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
